import { create } from "zustand";
import { persist } from "zustand/middleware";

export type RightTab = "details" | "chat" | "graph";
export type ChatScopeKind = "note" | "workspace";
export type WorkspaceApp = "notes" | "tasks" | "cards" | "graph";
export type CardMode = "cards" | "archive" | "trash";

interface UiState {
  treeCollapsed: boolean;
  rightCollapsed: boolean;
  treeWidth: number;
  rightWidth: number;
  focusMode: boolean;
  rightTab: RightTab;
  chatScope: ChatScopeKind;
  /** Three peer products sharing one data/sync foundation. */
  activeApp: WorkspaceApp;
  /** "overview" or a task-list item id. */
  activeTaskView: string;
  /** "all" or a Card board item id. */
  activeCardBoard: string;
  cardMode: CardMode;
  toggleTree: () => void;
  toggleRight: () => void;
  toggleFocusMode: () => void;
  setTreeWidth: (w: number) => void;
  setRightWidth: (w: number) => void;
  setRightTab: (tab: RightTab) => void;
  setActiveApp: (app: WorkspaceApp) => void;
  openTaskView: (view: string) => void;
  openCardBoard: (boardId: string) => void;
  setCardMode: (mode: CardMode) => void;
  /** Open the panel on a tab, widening it the first time chat is shown. */
  openRight: (tab: RightTab, scope?: ChatScopeKind) => void;
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

const RIGHT_MIN = 240;
const RIGHT_MAX = 720;
/** Narrower than this and a tool call chip is unreadable. */
const CHAT_COMFORT = 420;
/** A force-directed graph in the 240px minimum is unusable. */
const GRAPH_COMFORT = 320;

/** Tabs that need more room than the panel's default width. */
function comfortFor(tab: RightTab): number {
  if (tab === "chat") return CHAT_COMFORT;
  if (tab === "graph") return GRAPH_COMFORT;
  return 0;
}
/** Never squeeze the editor below a usable column. */
const rightMax = () => Math.min(RIGHT_MAX, Math.max(RIGHT_MIN, window.innerWidth - 560));

export const useUi = create<UiState>()(
  persist(
    (set) => ({
      treeCollapsed: false,
      rightCollapsed: true,
      treeWidth: 260,
      rightWidth: 280,
      focusMode: false,
      rightTab: "details",
      chatScope: "note",
      activeApp: "notes",
      activeTaskView: "overview",
      activeCardBoard: "all",
      cardMode: "cards",
      toggleTree: () => set((s) => ({ treeCollapsed: !s.treeCollapsed })),
      // Cards is the only app whose sidebar is optional. Graph in particular
      // must uncollapse: its sidebar IS its settings panel, so arriving at a
      // graph with no controls is a dead end.
      setActiveApp: (activeApp) => set({ activeApp, ...(activeApp === "cards" ? {} : { treeCollapsed: false }) }),
      openTaskView: (activeTaskView) => set({ activeApp: "tasks", activeTaskView, treeCollapsed: false }),
      openCardBoard: (activeCardBoard) => set({ activeApp: "cards", activeCardBoard, cardMode: "cards", treeCollapsed: false }),
      setCardMode: (cardMode) => set({ activeApp: "cards", cardMode }),
      toggleRight: () => set((s) => ({ rightCollapsed: !s.rightCollapsed })),
      toggleFocusMode: () =>
        set((s) => {
          const next = !s.focusMode;
          document.documentElement.classList.toggle("hn-focus-mode", next);
          return { focusMode: next };
        }),
      setTreeWidth: (w) => set({ treeWidth: clamp(w, 180, 480), treeCollapsed: false }),
      setRightWidth: (w) => set({ rightWidth: clamp(w, RIGHT_MIN, rightMax()), rightCollapsed: false }),
      setRightTab: (rightTab) =>
        set((s) => ({
          rightTab,
          rightWidth: Math.max(s.rightWidth, Math.min(comfortFor(rightTab), rightMax())),
        })),
      openRight: (tab, scope) =>
        set((s) => ({
          rightCollapsed: false,
          rightTab: tab,
          chatScope: scope ?? s.chatScope,
          rightWidth: Math.max(s.rightWidth, Math.min(comfortFor(tab), rightMax())),
        })),
    }),
    { name: "hn.ui" },
  ),
);
